/**
 * Consent bridge: inits analytics and marketing only when Axeptio consent is given.
 * Listens to cookies:complete (with replay) and consent:saved to apply consent state.
 */

import {
  setAnalyticsConsentGranted,
  setMarketingConsentGranted,
} from './consentState';
import { initAnalytics } from './analytics';
import { initTikTokPixel } from './tiktokPixel';
import {
  initPerformanceMonitoring,
  initLongTaskMonitoring,
} from './performance';

/** Google Consent Mode v2 payload from Axeptio cookies:complete / consent:saved */
interface AxeptioGcmPayload {
  $$scope?: string;
  $$duration?: number;
  $$googleConsentMode?: {
    analytics_storage?: string;
    ad_storage?: string;
    ad_user_data?: string;
    ad_personalization?: string;
    version?: number;
  };
}

declare global {
  interface Window {
    axeptioSDK?: {
      on: (
        event: string,
        handler: (payload: AxeptioGcmPayload, eventName?: string) => void,
        options?: { replay?: boolean; once?: boolean }
      ) => void;
    };
    _axcb?: Array<(sdk: any) => void>;
  }
}

/** Only init analytics/performance once */
let analyticsInitialized = false;
let marketingInitialized = false;

function isAnalyticsAllowed(payload: AxeptioGcmPayload | null | undefined): boolean {
  const gcm = payload?.$$googleConsentMode;
  return gcm?.analytics_storage === 'granted';
}

function isMarketingAllowed(payload: AxeptioGcmPayload | null | undefined): boolean {
  const gcm = payload?.$$googleConsentMode;
  return gcm?.ad_storage === 'granted' || gcm?.ad_personalization === 'granted';
}

function applyConsent(payload: AxeptioGcmPayload | null | undefined): void {
  const analyticsAllowed = isAnalyticsAllowed(payload);
  const marketingAllowed = isMarketingAllowed(payload);

  setAnalyticsConsentGranted(analyticsAllowed);
  setMarketingConsentGranted(marketingAllowed);

  if (analyticsAllowed && !analyticsInitialized) {
    analyticsInitialized = true;
    initAnalytics();
    initPerformanceMonitoring();
    initLongTaskMonitoring();
    if (import.meta.env.PROD) {
      console.log('[Consent] Analytics initialized (consent given)');
    }
  }

  if (marketingAllowed && !marketingInitialized) {
    marketingInitialized = true;
    initTikTokPixel();
    if (import.meta.env.PROD) {
      console.log('[Consent] Marketing initialized (TikTok Pixel loaded)');
    }
  }

  if (!analyticsAllowed && analyticsInitialized && import.meta.env.PROD) {
    console.log('[Consent] Analytics consent revoked - tracking disabled');
  }
}

function registerWithSdk(sdk: any): void {
  if (!sdk || typeof sdk.on !== 'function') return;

  // Replay: true so we get consent even if widget didn't show (e.g. returning visitor)
  // Payload is Google Consent Mode v2: { $$googleConsentMode: { analytics_storage, ad_storage, ... } }
  sdk.on('cookies:complete', function (...args: any[]) {
    const payload = args[0] as AxeptioGcmPayload | undefined;
    applyConsent(payload ?? null);
  }, { replay: true });
  sdk.on('consent:saved', function (...args: any[]) {
    const payload = args[0] as AxeptioGcmPayload | undefined;
    applyConsent(payload ?? null);
  });
}

/**
 * Start the consent bridge. Call once from main.tsx.
 * Waits for Axeptio SDK then inits analytics/marketing only when consent is given.
 */
export function startConsentBridge(): void {
  if (typeof window === 'undefined') return;

  if (window.axeptioSDK) {
    registerWithSdk(window.axeptioSDK);
    return;
  }

  window._axcb = window._axcb || [];
  window._axcb.push((sdk: any) => {
    registerWithSdk(sdk);
  });
}
